import { type UIEvent, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@needl-ai/common/components/ui/button";
import { Checkbox } from "@needl-ai/common/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@needl-ai/common/components/ui/dialog";
import { Label } from "@needl-ai/common/components/ui/label";
import { cn } from "@needl-ai/common/lib/utils";

const STORAGE_KEY = "pwc_disclaimer_accepted_date";

const getTodayDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const PwCDisclaimer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const acceptedDate = localStorage.getItem(STORAGE_KEY);
    if (acceptedDate !== getTodayDate()) {
      setHasScrolledToBottom(false);
      setHasAgreed(false);
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el && el.scrollHeight <= el.clientHeight) {
          setHasScrolledToBottom(true);
        }
      });
    }
  }, [isOpen]);

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, getTodayDate());
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl gap-0 overflow-hidden border-t-4 border-t-primary p-0 focus:outline-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Terms of Use
            </DialogTitle>
            <DialogDescription className="text-sm text-foreground/80 pt-1">
              By accessing{" "}
              <span className="text-primary font-semibold">
                Client Intelligence and Insights Hub
              </span>
              , you acknowledge and agree to the following legally binding
              terms.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          tabIndex={0}
          role="region"
          aria-label="Terms of Use content"
          className="max-h-[400px] overflow-y-auto divide-y border-y px-6 text-[13px] leading-relaxed text-muted-foreground focus:outline-none"
        >
          <div className="py-4">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              1. Permitted Use
            </p>
            <p>
              You may access and use Client Intelligence and Insights Hub solely
              for authorized, legitimate business purposes within the scope of
              your role and responsibilities. All use must comply with:
            </p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>The PwC Global Code of Conduct</li>
              <li>
                Applicable data protection, confidentiality, and professional
                standards
              </li>
              <li>
                All relevant local and cross-border regulatory requirements
              </li>
            </ul>
          </div>

          <div className="py-4">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              2. Confidentiality and Data Protection
            </p>
            <p>
              Client Intelligence and Insights Hub contains confidential,
              commercially sensitive, and potentially legally privileged
              information, including contract terms, pricing, and client data.
              You agree to:
            </p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Maintain strict confidentiality of all data accessed</li>
              <li>
                Process personal data only where necessary and in compliance
                with applicable privacy laws (including GDPR or equivalent)
              </li>
              <li>
                Refrain from extracting, aggregating, or reusing data beyond
                defined business purposes
              </li>
            </ul>
          </div>

          <div className="py-4">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              3. Restrictions on Disclosure and Use
            </p>
            <p>You must not, without prior written authorization:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>
                Disclose Client Intelligence and Insights Hub data to any
                external party, including clients, vendors, or third parties
              </li>
              <li>
                Share sensitive contract-level information outside authorized
                internal stakeholders
              </li>
              <li>
                Use information for personal benefit, competitive advantage, or
                any non-business purpose
              </li>
            </ul>
            <p className="mt-2">
              Client Intelligence and Insights Hub outputs must not be relied
              upon as a substitute for formal contractual documentation or legal
              advice.
            </p>
          </div>

          <div className="py-4">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              4. Data Security and System Use
            </p>
            <p>You agree not to:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>
                Download, store, or transfer data to unauthorized systems or
                devices
              </li>
              <li>
                Circumvent system controls, access rights, or security
                mechanisms
              </li>
              <li>
                Combine Client Intelligence and Insights Hub data with external
                datasets in a manner that could compromise confidentiality
              </li>
            </ul>
            <p className="mt-2">
              Access to Client Intelligence and Insights Hub is monitored and
              subject to audit and review.
            </p>
          </div>

          <div className="py-4">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              5. Incident Reporting and External Requests
            </p>
            <p>You must:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>
                Immediately report any suspected or actual breach, misuse, or
                unauthorized access
              </li>
              <li>
                Not respond directly to external or regulatory requests for
                data; all such requests must be escalated to Legal / Risk / Data
                Protection teams
              </li>
            </ul>
          </div>

          <div className="py-4">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              6. Enforcement and Liability
            </p>
            <p>
              Access is conditional upon compliance with these terms. Any breach
              may result in:
            </p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Immediate suspension or termination of access</li>
              <li>
                Disciplinary action, up to and including termination of
                employment
              </li>
              <li>
                Potential civil, regulatory, or criminal liability under
                applicable laws
              </li>
            </ul>
          </div>

          <div className="py-4 bg-muted/30 -mx-6 px-6">
            <p className="text-sm font-semibold text-foreground mb-1.5">
              Acknowledgement
            </p>
            <p>By selecting &ldquo;Accept,&rdquo; you confirm that you:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Have read and understood these Terms of Use</li>
              <li>
                Agree to comply with all obligations and restrictions outlined
                above
              </li>
              <li>
                Accept accountability for any misuse or unauthorized disclosure
                of Client Intelligence and Insights Hub data
              </li>
            </ul>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          {!hasScrolledToBottom && (
            <p className="text-center text-sm font-medium text-primary">
              Scroll to the end to continue
            </p>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="pwc-disclaimer-agree"
              disabled={!hasScrolledToBottom}
              checked={hasAgreed}
              onCheckedChange={(checked) => setHasAgreed(checked === true)}
              className="focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <Label
              htmlFor="pwc-disclaimer-agree"
              className={cn(
                "text-sm",
                !hasScrolledToBottom
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              )}
            >
              I have read and agree to the Terms of Use.
            </Label>
          </div>

          <div className="flex justify-end">
            <Button
              disabled={!hasAgreed}
              onClick={handleAccept}
              className="px-8"
            >
              Accept
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PwCDisclaimer;
